import { z } from 'zod'

import {
  ActivityLogActorType,
  ActivityLogChangeType,
  ActivityLogDocumentType,
  ActivityLogPropertyChangeType,
  ActivityLogSort,
} from '../constants/activityLog'
import { listQueryZodFields } from './system'
import { dbEntitySchema } from './system'

const emptyStringToUndefined = (value: unknown) =>
  value === '' || value === null ? undefined : value

export const activityLogChangeSchema = z.object({
  changeType: z.nativeEnum(ActivityLogPropertyChangeType),
  keyPath: z.string(),
  oldValue: z.any().optional(),
  newValue: z.any().optional(),
})

export const activityLogAdmissionGroupContextSchema = z.object({
  admissionGroupId: z.string(),
  customerId: z.string().nullish(),
  name: z.string().nullish(),
  email: z.string().nullish(),
  phone: z.string().nullish(),
  inventoryId: z.string().nullish(),
  inventoryName: z.string().nullish(),
  attributionId: z.string().nullish(),
  attributionName: z.string().nullish(),
})

/** Strips legacy/partial embedded docs missing admissionGroupId before validation. */
export const activityLogAdmissionGroupContextFieldSchema = z.preprocess(
  (value) => {
    if (value == null || typeof value !== 'object') {
      return undefined
    }

    const context = value as { admissionGroupId?: string | null }
    if (!context.admissionGroupId) {
      return undefined
    }

    return value
  },
  activityLogAdmissionGroupContextSchema.optional()
)

export const activityLogInventoryContextSchema = z.object({
  inventoryId: z.string(),
  name: z.string().nullish(),
  type: z.string().nullish(),
  date: z.coerce.date().nullish(),
  eventId: z.string().nullish(),
  eventName: z.string().nullish(),
})

export const activityLogInventoryContextFieldSchema = z.preprocess((value) => {
  if (value == null || typeof value !== 'object') {
    return undefined
  }

  const context = value as { inventoryId?: string | null }
  if (!context.inventoryId) {
    return undefined
  }

  return value
}, activityLogInventoryContextSchema.optional())

export const activityLogEventContextSchema = z.object({
  eventId: z.string(),
  name: z.string().nullish(),
  dateStart: z.coerce.date().nullish(),
  dateEnd: z.coerce.date().nullish(),
  timezone: z.string().nullish(),
})

export const activityLogEventContextFieldSchema = z.preprocess((value) => {
  if (value == null || typeof value !== 'object') {
    return undefined
  }

  const context = value as { eventId?: string | null }
  if (!context.eventId) {
    return undefined
  }

  return value
}, activityLogEventContextSchema.optional())

export const activityLogSchema = dbEntitySchema.extend({
  documentType: z.nativeEnum(ActivityLogDocumentType),
  documentId: z.string(),
  type: z.nativeEnum(ActivityLogChangeType),
  changes: z.array(activityLogChangeSchema),
  managementUser: z.string().nullish(),
  managementUserName: z.string().nullish(),
  managementUserEmail: z.string().nullish(),
  managementUserProfileImage: z.string().nullish(),
  organization: z.string(),
  venue: z.string().nullish(),
  venueName: z.string().nullish(),
  created: z.date(),
  modified: z.date(),
  displayText: z.string().optional(),
  admissionGroupContext: activityLogAdmissionGroupContextFieldSchema,
  inventoryContext: activityLogInventoryContextFieldSchema,
  eventContext: activityLogEventContextFieldSchema,
  actorType: z.nativeEnum(ActivityLogActorType).nullish(),
  actorName: z.string().nullish(),
  actorEmail: z.string().nullish(),
})

export const activityLogCreateSchema = z.object({
  organizationId: z.string().nullish(),
  venueId: z.string().nullish(),
  managementUserId: z.string().nullish(),
  allowedKeys: z.array(z.string()).optional(),
  documentType: z.nativeEnum(ActivityLogDocumentType),
  documentId: z.string(),
  oldValue: z.any(),
  newValue: z.any(),
  type: z.nativeEnum(ActivityLogChangeType),
})

export const activityLogListArgsSchema = z.object({
  search: z.preprocess(emptyStringToUndefined, z.string().optional()),
  documentType: z.preprocess(
    emptyStringToUndefined,
    z.nativeEnum(ActivityLogDocumentType).optional()
  ),
  documentId: z.preprocess(emptyStringToUndefined, z.string().optional()),
  organizationId: z.string().optional(),
  venueId: z.preprocess(
    emptyStringToUndefined,
    z.union([z.string(), z.array(z.string())]).optional()
  ),
  managementUserId: z.preprocess(emptyStringToUndefined, z.string().optional()),
  actorType: z.preprocess((value) => {
    if (value == null || value === '') return undefined

    const values = Array.isArray(value) ? value : [value]
    const filtered = values.filter((item) => item !== '' && item != null)

    return filtered.length ? filtered : undefined
  }, z.array(z.nativeEnum(ActivityLogActorType)).optional()),
  createdStart: z.coerce.date().optional(),
  createdEnd: z.coerce.date().optional(),
})

export const activityLogListResponseSchema = z.object({
  data: z.array(activityLogSchema),
})
